import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';
import { formatDateTime } from '../utils/format';

const CATEGORY_LABELS = {
  'legal': '法律合规', 'finance': '财务审计', 'business': '商务拓展',
  'engineering': '工程技术', 'hr': '人力资源', 'supply-chain': '供应链', 'other': '其他',
};

function StarRating({ rating, size = '1rem' }) {
  const full = Math.floor(rating);
  const half = rating - full >= 0.3;
  return (
    <span style={{ color: '#f59e0b', fontSize: size, letterSpacing: 1 }}>
      {'★'.repeat(full)}{half ? '½' : ''}{'☆'.repeat(5 - full - (half ? 1 : 0))}
    </span>
  );
}

function StarInput({ value, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 4 }}>
      {[1, 2, 3, 4, 5].map(n => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            fontSize: '1.5rem', color: n <= value ? '#f59e0b' : '#d1d5db',
            padding: 0,
          }}
        >★</button>
      ))}
    </div>
  );
}

function ExpertDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [expert, setExpert] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState('');

  // 评价表单
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewContent, setReviewContent] = useState('');
  const [reviewAnon, setReviewAnon] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // 编辑资料（专家本人）
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchExpert();
    fetchReviews();
  }, [id]);

  const fetchExpert = async () => {
    try {
      const res = await api.get(`/experts/${id}`);
      setExpert(res.data);
    } catch { }
    setLoading(false);
  };

  const fetchReviews = async () => {
    try {
      const res = await api.get(`/experts/${id}/reviews`);
      setReviews(res.data || []);
    } catch { }
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post(`/experts/${id}/reviews`, {
        rating: reviewRating, content: reviewContent, isAnonymous: reviewAnon,
      });
      setShowReviewForm(false);
      setReviewContent('');
      setReviewRating(5);
      fetchReviews();
      fetchExpert(); // 刷新评分
      showToast('评价成功');
    } catch (err) {
      showToast(err.response?.data?.error || '评价失败');
    }
    setSubmitting(false);
  };

  const openEdit = () => {
    setEditing({
      displayName: expert.displayName || '',
      title: expert.title || '',
      category: expert.category || 'legal',
      experience: expert.experience || '',
      credentials: expert.credentials || '',
      specialties: expert.specialties || '',
      bio: expert.bio || '',
      contactInfo: expert.contactInfo || '',
    });
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await api.put(`/experts/${id}`, editing);
      fetchExpert();
      setEditing(null);
      showToast('资料更新成功');
    } catch (err) {
      showToast(err.response?.data?.error || '更新失败');
    }
    setSaving(false);
  };

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2500);
  };

  const isAdmin = user && user.role === 'admin';

  if (loading) return <div className="loading">加载中...</div>;
  if (!expert) return <div className="empty-state"><p>专家不存在</p></div>;

  return (
    <div className="container" style={{ paddingTop: 20, paddingBottom: 60, maxWidth: 800 }}>
      <Link to="/experts" style={{ color: 'var(--text-light)', textDecoration: 'none', fontSize: '0.9rem' }}>
        ← 返回专家列表
      </Link>

      {/* 专家卡片 */}
      <div className="card" style={{ padding: 28, marginTop: 16 }}>
        <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div style={{
            width: 72, height: 72, borderRadius: '50%',
            background: 'var(--primary-light)', color: 'var(--primary)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '2rem', flexShrink: 0,
          }}>
            ⚖️
          </div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <h1 style={{ margin: 0, fontSize: '1.4rem' }}>{expert.displayName}</h1>
              <span className="tag tag-purple">{CATEGORY_LABELS[expert.category] || expert.category}</span>
            </div>
            {expert.title && (
              <div style={{ fontSize: '1rem', color: 'var(--text-secondary)', marginTop: 6 }}>{expert.title}</div>
            )}
            <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
              <StarRating rating={expert.avgRating || 0} size="1.1rem" />
              <span style={{ fontSize: '0.88rem', color: 'var(--text-light)' }}>
                {Number(expert.avgRating || 0).toFixed(1)} · {expert.reviewCount || 0} 条评价
              </span>
            </div>
          </div>
        </div>

        <div style={{ marginTop: 20, display: 'grid', gap: 12 }}>
          {expert.experience && (
            <div style={{ fontSize: '0.9rem' }}><strong>从业经验：</strong>{expert.experience}</div>
          )}
          {expert.credentials && (
            <div style={{ fontSize: '0.9rem' }}><strong>资质认证：</strong>{expert.credentials}</div>
          )}
          {expert.specialties && (
            <div style={{ fontSize: '0.9rem' }}><strong>擅长领域：</strong>{expert.specialties}</div>
          )}
        </div>

        {expert.bio && (
          <div style={{ marginTop: 16, padding: 16, background: 'var(--border-light)', borderRadius: 8 }}>
            <div style={{ fontSize: '0.88rem', fontWeight: 600, marginBottom: 8 }}>专家介绍</div>
            <div style={{ fontSize: '0.9rem', lineHeight: 1.7, color: 'var(--text-secondary)', whiteSpace: 'pre-line' }}>
              {expert.bio}
            </div>
          </div>
        )}

        {/* 操作按钮 */}
        <div style={{ marginTop: 20, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {user && (
            <button
              className="btn btn-primary"
              onClick={() => navigate(`/messages?to=${expert.userId}&name=${encodeURIComponent(expert.displayName)}&subject=${encodeURIComponent('预约咨询 - ' + expert.displayName)}`)}
            >
              预约咨询
            </button>
          )}
          {!user && (
            <Link to="/login" className="btn btn-primary">登录后预约咨询</Link>
          )}
          {isAdmin && (
            <button className="btn btn-ghost" onClick={openEdit}>编辑资料</button>
          )}
        </div>
      </div>

      {/* 评价区域 */}
      <div style={{ marginTop: 32 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: '1.2rem' }}>用户评价 ({reviews.length})</h2>
          {user && (
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => setShowReviewForm(!showReviewForm)}
            >
              {showReviewForm ? '取消' : '写评价'}
            </button>
          )}
        </div>

        {/* 评价表单 */}
        {showReviewForm && (
          <form onSubmit={handleReviewSubmit} className="card" style={{ padding: 20, marginBottom: 20 }}>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>评分</label>
              <StarInput value={reviewRating} onChange={setReviewRating} />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>评价内容</label>
              <textarea
                className="form-input"
                rows={3}
                value={reviewContent}
                onChange={e => setReviewContent(e.target.value)}
                placeholder="分享你的咨询体验..."
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', color: 'var(--text-light)', cursor: 'pointer' }}>
                <input type="checkbox" checked={reviewAnon} onChange={e => setReviewAnon(e.target.checked)} />
                匿名评价
              </label>
              <button className="btn btn-primary btn-sm" type="submit" disabled={submitting}>
                {submitting ? '提交中...' : '提交评价'}
              </button>
            </div>
          </form>
        )}

        {/* 评价列表 */}
        {reviews.length === 0 ? (
          <div style={{ padding: '32px 0', textAlign: 'center', color: 'var(--text-light)' }}>
            暂无评价，成为第一个评价的人
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {reviews.map(r => (
              <div key={r.id} className="card" style={{ padding: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{
                      width: 30, height: 30, borderRadius: '50%', background: 'var(--border-light)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem',
                    }}>
                      {(r.authorName || '?')[0]}
                    </div>
                    <span style={{ fontWeight: 600, fontSize: '0.88rem' }}>{r.authorName}</span>
                    <StarRating rating={r.rating} size="0.85rem" />
                  </div>
                  <span style={{ fontSize: '0.78rem', color: 'var(--text-light)' }}>{formatDateTime(r.createdAt)}</span>
                </div>
                {r.content && <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', lineHeight: 1.6 }}>{r.content}</div>}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 编辑资料弹窗 */}
      {editing && (
        <div
          onClick={() => !saving && setEditing(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 20, overflow: 'auto' }}
        >
          <div onClick={e => e.stopPropagation()} className="edit-modal-content">
            <h2 style={{ margin: '0 0 16px', fontSize: '1.2rem' }}>编辑专家资料</h2>
            <div style={{ display: 'grid', gap: 12 }}>
              <div className="edit-row-pair">
                <div>
                  <label style={lblStyle}>显示名称</label>
                  <input className="form-input" value={editing.displayName} onChange={e => setEditing({ ...editing, displayName: e.target.value })} />
                </div>
                <div>
                  <label style={lblStyle}>头衔</label>
                  <input className="form-input" value={editing.title} onChange={e => setEditing({ ...editing, title: e.target.value })} />
                </div>
              </div>
              <div className="edit-row-pair">
                <div>
                  <label style={lblStyle}>从业经验</label>
                  <input className="form-input" value={editing.experience} onChange={e => setEditing({ ...editing, experience: e.target.value })} />
                </div>
                <div>
                  <label style={lblStyle}>资质认证</label>
                  <input className="form-input" value={editing.credentials} onChange={e => setEditing({ ...editing, credentials: e.target.value })} />
                </div>
              </div>
              <div>
                <label style={lblStyle}>擅长领域</label>
                <input className="form-input" value={editing.specialties} onChange={e => setEditing({ ...editing, specialties: e.target.value })} />
              </div>
              <div>
                <label style={lblStyle}>个人介绍</label>
                <textarea className="form-input" rows={5} value={editing.bio} onChange={e => setEditing({ ...editing, bio: e.target.value })} />
              </div>
              <div>
                <label style={lblStyle}>联系信息说明</label>
                <input className="form-input" value={editing.contactInfo} onChange={e => setEditing({ ...editing, contactInfo: e.target.value })} />
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
              <button className="btn btn-secondary" onClick={() => setEditing(null)} disabled={saving}>取消</button>
              <button className="btn btn-primary" onClick={handleSaveProfile} disabled={saving}>
                {saving ? '保存中...' : '保存'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}

const lblStyle = { display: 'block', fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: 6 };

export default ExpertDetail;
